<template>
    <div>
        <section class="card vehicle-badges-card" aria-labelledby="vehicle-badges-title">
            <header class="vehicle-badges-header">
                <div class="vehicle-badges-heading">
                    <span class="vehicle-badges-heading-icon" aria-hidden="true">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8"
                            stroke-linecap="round" stroke-linejoin="round">
                            <rect x="3" y="5" width="18" height="14" rx="3" />
                            <path d="M3 10h18M7 15h4" />
                        </svg>
                    </span>
                    <div>
                        <h2 id="vehicle-badges-title" class="vehicle-badges-title">Badges et chauffeurs</h2>
                        <p class="vehicle-badges-subtitle">Associés à ce véhicule</p>
                    </div>
                </div>
    
                <div class="vehicle-badges-counts" aria-label="Nombre de badges et de chauffeurs">
                    <span class="vehicle-badges-count">
                        <strong>{{ badges.length }}</strong> badge{{ badges.length > 1 ? 's' : '' }}
                    </span>
                    <span class="vehicle-badges-count">
                        <strong>{{ driverCount }}</strong> chauffeur{{ driverCount > 1 ? 's' : '' }}
                    </span>
                </div>
            </header>
    
            <div v-if="!badges.length" class="vehicle-badges-empty">
                <span class="vehicle-badges-empty-icon" aria-hidden="true">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6"
                        stroke-linecap="round" stroke-linejoin="round">
                        <rect x="3" y="5" width="18" height="14" rx="3" />
                        <path d="M3 10h18M7 15h4" />
                    </svg>
                </span>
                <strong>Aucun badge associé</strong>
                <p>Les badges liés à ce véhicule et leurs chauffeurs apparaîtront ici.</p>
            </div>
    
            <div v-else class="vehicle-badges-list">
                <div v-for="badge in badges" :key="badge.id" class="vehicle-badge-row">
                    <div class="vehicle-badge-column">
                        <div class="vehicle-badge-eyebrow">
                            <span>Badge</span>
                            <span class="vehicle-badge-status" :class="badgeStatusClass(badge.status)">
                                {{ badgeStatusLabel(badge.status) }}
                            </span>
                        </div>
                        <div class="vehicle-badge-identity">
                            <span class="vehicle-badge-symbol" aria-hidden="true">
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8"
                                    stroke-linecap="round" stroke-linejoin="round">
                                    <rect x="3" y="5" width="18" height="14" rx="3" />
                                    <path d="M3 10h18M7 15h4" />
                                </svg>
                            </span>
                            <div class="vehicle-badge-details">
                                <strong class="vehicle-badge-reference">{{ badgeReference(badge) }}</strong>
                                <span class="vehicle-badge-number">N° {{ badgeNumber(badge) }}</span>
                            </div>
                        </div>
                    </div>
    
                    <div class="vehicle-driver-column">
                        <div class="vehicle-badge-eyebrow">Chauffeur</div>
                        <div v-if="badge.driver" class="vehicle-driver-identity">
                            <span class="vehicle-driver-avatar" aria-hidden="true">
                                {{ driverInitials(badge.driver) }}
                            </span>
                            <div class="vehicle-driver-details">
                                <strong class="vehicle-driver-name">{{ driverName(badge.driver) }}</strong>
                                <span class="vehicle-driver-state" :class="driverStatusClass(badge.driver.status)">
                                    <span class="vehicle-driver-state-dot" aria-hidden="true"></span>
                                    {{ driverStatusLabel(badge.driver.status) }}
                                </span>
                            </div>
                        </div>
                        <div v-else class="vehicle-driver-unassigned">
                            Aucun chauffeur associé
                        </div>
                    </div>
                </div>
            </div>
        </section>
    </div>
</template>

<script>
export default {
    props: {
        badges: {
            type: Array,
            default: () => []
        }
    },
    computed: {
        driverCount() {
            return new Set(
                this.badges
                    .map(badge => badge.driver?.id)
                    .filter(id => id !== null && id !== undefined)
            ).size;
        }
    },
    methods: {
        badgeReference(badge) {
            return String(badge.ref ?? '').trim() || `Badge #${badge.id}`;
        },
        badgeNumber(badge) {
            return String(badge.number ?? '').trim() || 'Non renseigné';
        },
        badgeStatusLabel(status) {
            return {
                ACTIVE: 'Actif',
                DISABLED: 'Désactivé',
                NEW: 'Nouveau'
            }[status] || 'Statut inconnu';
        },
        badgeStatusClass(status) {
            return {
                ACTIVE: 'is-active',
                DISABLED: 'is-disabled',
                NEW: 'is-new'
            }[status] || 'is-unknown';
        },
        driverName(driver) {
            return [driver.firstName, driver.lastName].filter(Boolean).join(' ').trim()
                || `Chauffeur #${driver.id}`;
        },
        driverInitials(driver) {
            const initials = [driver.firstName, driver.lastName]
                .filter(Boolean)
                .map(name => String(name).trim().charAt(0).toUpperCase())
                .join('');
            return initials || 'CH';
        },
        driverStatusLabel(status) {
            return {
                ACTIVE: 'Actif',
                INACTIVE: 'Inactif'
            }[status] || 'Statut inconnu';
        },
        driverStatusClass(status) {
            return status === 'ACTIVE' ? 'is-active' : 'is-inactive';
        }
    }
}
</script>

<style scoped>
.vehicle-badges-card {
    width: 100%;
    min-width: 0;
    overflow: hidden;
    border: 1px solid #e6eaf1;
    border-radius: 16px;
    background: #fff;
    box-shadow: 0 8px 24px rgba(28, 43, 67, 0.09);
}

.vehicle-badges-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 0.85rem 1.2rem;
    padding: 1.05rem 1.15rem;
    border-bottom: 1px solid #edf0f4;
    background: linear-gradient(120deg, #fff 40%, #f5f9ff 100%);
}

.vehicle-badges-heading {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    min-width: 0;
}

.vehicle-badges-heading-icon {
    display: flex;
    flex: 0 0 40px;
    align-items: center;
    justify-content: center;
    height: 40px;
    border-radius: 12px;
    background: #e9f1ff;
    color: #2860ac;
}

.vehicle-badges-heading-icon svg,
.vehicle-badges-empty-icon svg {
    width: 21px;
    height: 21px;
}

.vehicle-badges-title {
    margin: 0;
    color: #182439;
    font-size: 1.04rem;
    font-weight: 700;
    line-height: 1.3;
}

.vehicle-badges-subtitle {
    margin: 0.15rem 0 0;
    color: #7a8699;
    font-size: 0.78rem;
    line-height: 1.4;
}

.vehicle-badges-counts {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 0.4rem;
}

.vehicle-badges-count {
    display: inline-flex;
    align-items: center;
    gap: 0.3rem;
    padding: 0.34rem 0.6rem;
    border: 1px solid #e7ebf2;
    border-radius: 99px;
    background: #fff;
    color: #69778b;
    font-size: 0.77rem;
    white-space: nowrap;
}

.vehicle-badges-count strong {
    color: #253b58;
    font-weight: 700;
}

.vehicle-badges-list {
    min-width: 0;
}

.vehicle-badge-row {
    display: grid;
    grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
    gap: 1.2rem;
    align-items: start;
    padding: 0.95rem 1.15rem;
    transition: background-color 0.15s ease;
}

.vehicle-badge-row + .vehicle-badge-row {
    border-top: 1px solid #edf0f4;
}

.vehicle-badge-row:hover {
    background: #f9fbfe;
}

.vehicle-badge-column,
.vehicle-driver-column {
    min-width: 0;
}

.vehicle-badge-eyebrow {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 0.45rem;
    min-height: 20px;
    margin-bottom: 0.5rem;
    color: #8190a4;
    font-size: 0.67rem;
    font-weight: 700;
    letter-spacing: 0.075em;
    text-transform: uppercase;
}

.vehicle-badge-status {
    padding: 0.18rem 0.5rem;
    border-radius: 99px;
    font-size: 0.67rem;
    font-weight: 600;
    letter-spacing: 0;
    line-height: 1.2;
    text-transform: none;
}

.vehicle-badge-status.is-active {
    background: #e6f5eb;
    color: #287449;
}

.vehicle-badge-status.is-disabled {
    background: #f0f2f5;
    color: #707b8b;
}

.vehicle-badge-status.is-new {
    background: #fff3dc;
    color: #97671c;
}

.vehicle-badge-status.is-unknown {
    background: #eef2f7;
    color: #64748b;
}

.vehicle-badge-identity,
.vehicle-driver-identity {
    display: flex;
    align-items: center;
    gap: 0.65rem;
    min-width: 0;
}

.vehicle-badge-symbol,
.vehicle-driver-avatar {
    display: flex;
    flex: 0 0 35px;
    align-items: center;
    justify-content: center;
    height: 35px;
    border-radius: 10px;
    background: #eef3fc;
    color: #386ba9;
}

.vehicle-badge-symbol svg {
    width: 18px;
    height: 18px;
}

.vehicle-driver-avatar {
    border-radius: 50%;
    background: #e8eff7;
    color: #4a6586;
    font-size: 0.76rem;
    font-weight: 700;
    letter-spacing: 0.025em;
}

.vehicle-badge-details,
.vehicle-driver-details {
    display: flex;
    flex-direction: column;
    gap: 0.15rem;
    min-width: 0;
}

.vehicle-badge-reference,
.vehicle-driver-name {
    color: #1d2c43;
    font-size: 0.9rem;
    font-weight: 700;
    line-height: 1.3;
    overflow-wrap: anywhere;
}

.vehicle-badge-number,
.vehicle-driver-unassigned {
    color: #7a8798;
    font-size: 0.79rem;
    line-height: 1.4;
    overflow-wrap: anywhere;
}

.vehicle-driver-state {
    display: inline-flex;
    align-items: center;
    gap: 0.35rem;
    color: #6e7b8d;
    font-size: 0.75rem;
    line-height: 1.4;
}

.vehicle-driver-state-dot {
    width: 6px;
    height: 6px;
    flex: 0 0 6px;
    border-radius: 50%;
    background: #95a0af;
}

.vehicle-driver-state.is-active .vehicle-driver-state-dot {
    background: #36a66b;
}

.vehicle-driver-unassigned {
    padding: 0.45rem 0;
    font-style: italic;
}

.vehicle-badges-empty {
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 1.8rem 1.2rem 2rem;
    text-align: center;
}

.vehicle-badges-empty-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 44px;
    height: 44px;
    margin-bottom: 0.65rem;
    border-radius: 14px;
    background: #f0f4f9;
    color: #8495ad;
}

.vehicle-badges-empty strong {
    color: #34435b;
    font-size: 0.9rem;
}

.vehicle-badges-empty p {
    max-width: 285px;
    margin: 0.3rem 0 0;
    color: #8290a2;
    font-size: 0.8rem;
    line-height: 1.5;
}

/* Sur les écrans intermédiaires, la colonne Bootstrap de droite est plus étroite. */
@media (max-width: 1199.98px) {
    .vehicle-badge-row {
        grid-template-columns: minmax(0, 1fr);
        gap: 0.75rem;
    }

    .vehicle-driver-column {
        padding-top: 0.65rem;
        border-top: 1px dashed #e5eaf1;
    }
}

@media (max-width: 575.98px) {
    .vehicle-badges-header {
        padding: 0.9rem;
    }

    .vehicle-badge-row {
        padding: 0.9rem;
    }

    .vehicle-badges-counts {
        width: 100%;
    }
}
</style>
